// in manage-statuses-dialog.component.ts

import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StatusGroup, StatusReason, PixelData, ReferralLayer } from '../charts/pixel-map/pixel-map.component';
import { StatusSelectorComponent } from '../status-selector/status-selector.component';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop'; // Import this
// import patientJourneyData from '../../../../../../../data/patient_journey_data_with_source_name.json_shortened.json';
import patientJourneyData from '../../../components/shared/charts/pixel-map/patient_journey_data_super_shortened.json';

interface JourneyEvent {
  STATUS_REASON_CODE: string;
  STATUS_DATE: string;
  JOURNEY_STOP: string;
  CURRENT_REFERRAL_STATUS: string;
  SP_DESCRIPTION: string;
}

export interface DialogData {
  hierarchy: ReferralLayer[];
  focusOn?: PixelData;
  updateCallback: (updatedHierarchy: ReferralLayer[]) => void;
  patientJourneyMap: Map<string, JourneyEvent[]>;
  ruleSet?: RuleSet;
}

// A single, unified Rule interface
export interface Rule {
  type: 'rule'; // Differentiator for the template
  field: string;
  operator: string;
  // For standard property rules
  value?: any;
  // For 'duration between' rules
  startStatus?: StatusReason[];
  endStatus?: StatusReason[];
  days?: number;
  durationOperator?: 'gt' | 'lt';
  // --- NEW: Extended Duration Properties ---
  operatorType?: 'BETWEEN' | '=' | '<=' | '>=' | '<' | '>';
  min_days?: number;
  max_days?: number;
  
  enabled: boolean;
  isEditing: boolean;
  rule_type?: 'Advanced' | 'Basic' | 'Sequential';
}

export interface RuleSet {
  type: 'ruleset';
  condition: 'and' | 'or';
  rules: (Rule | RuleSet)[];
  // New UI properties
  isCollapsed: boolean;
  name?: string; // Added name property
  enabled?: boolean; // NEW: Track enabled state for checkbox binding
}

export type QueryItem = Rule | RuleSet;


export interface SavedQueryItem {
  id: string;
  name: string;
  isActive: boolean;
  isSelected: boolean;
  sharedWithCount: number;
  groupCount: number;
  rulesetCount: number;
  ruleCount: number;
  queryData: RuleSet; // <--- NOW REQUIRED
}

export interface FieldDefinition {
  value: string;
  name: string;
  type: 'status' | 'date' | 'category';
  operators: string[];
  options?: { name: string, value: string }[];
}

@Component({
  selector: 'app-manage-statuses-dialog',
  templateUrl: './manage-statuses-dialog.component.html',
  styleUrls: ['./manage-statuses-dialog.component.scss']
})
export class ManageStatusesDialogComponent implements OnInit {
  @ViewChild('statusSelector') statusSelector!: StatusSelectorComponent;

  // Simplified properties
  public activeTab: 'statuses' | 'query' | 'list' | 'share' | 'ai' = 'statuses';
  public localHierarchy!: ReferralLayer[];
  public baseHierarchy!: ReferralLayer[];
  public ruleSet: RuleSet = { type: 'ruleset', condition: 'and', rules: [], isCollapsed: false, name: 'Group #1' };
  public fieldDefinitions: FieldDefinition[] = [];

  // Share View Properties
  public selectedQuery: SavedQueryItem | null = null;
  public searchQuery: string = '';
  public users = [
    { name: 'Liam Parker', selected: false },
    { name: 'Hunter Reeves', selected: false },
    { name: 'Isaac Spencer', selected: false },
    { name: 'Ethan Sullivan', selected: true },
    { name: 'Lucas Palmer', selected: false },
    { name: 'Dylan Walsh', selected: false },
    { name: 'Noah Briggs', selected: false },
    { name: 'Jackson Cole', selected: false },
    { name: 'Brandon Keller', selected: false },
    { name: 'Caleb Foster', selected: false },
    { name: 'Wyatt Turner', selected: false },
    { name: 'Samuel Norris', selected: false },
    { name: 'Owen Marshall', selected: true },
    { name: 'Gavin Matthews', selected: false },
    { name: 'Elias Grant', selected: false },
    { name: 'Logan Hayes', selected: false },
    { name: 'Connor Price', selected: false },
    { name: 'Trevor Miles', selected: false },
    { name: 'Aiden Brooks', selected: false },
    { name: 'Austin Reed', selected: false },
    { name: 'Carter Wilson', selected: true },
    { name: 'Nathan Drake', selected: false },
    { name: 'Mason Blake', selected: false },
    { name: 'Christopher Lane', selected: false },
  ];

  isLoading = true;

  constructor(
    public dialogRef: MatDialogRef<ManageStatusesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) { }

  ngOnInit(): void {
    // A single, faster deep copy.
    this.localHierarchy = this.cloneHierarchy(this.data.hierarchy);
    this.baseHierarchy = this.cloneHierarchy(this.data.hierarchy);
    this.snapshotHierarchy = this.cloneHierarchy(this.data.hierarchy); // Initial snapshot

    // Revert Logic: When dialog closes, ensure the chart reflects the *committed* state (snapshot).
    // If user Applied, snapshot is new. If not, snapshot is original.
    this.dialogRef.beforeClosed().subscribe(() => {
       this.data.updateCallback(this.snapshotHierarchy);
    });

    this.initializeFieldDefinitions();

    this.initializeFieldDefinitions();

    if (this.data.ruleSet) {
      // Load the persisted/passed ruleSet
      this.ruleSet = JSON.parse(JSON.stringify(this.data.ruleSet));
      // --- NEW: Hydrate rules (restore UI friendly operators and objects) ---
      this.hydrateRuleSet(this.ruleSet);
    } else if (!this.ruleSet.rules.length) {
      // Create the "Primary Ruleset" that is visible to the user
      const primaryGroup: RuleSet = {
        type: 'ruleset',
        condition: 'and',
        rules: [],
        isCollapsed: false,
        name: 'Group #1',
        enabled: true
      };

      // Add a default rule inside that primary group
      this.addRule(primaryGroup);

      // Add the primary group to our invisible root container
      this.ruleSet.rules.push(primaryGroup);
    }

    this.isLoading = false;
  }

  // --- NEW: Method to dynamically create field definitions and their options ---
  private initializeFieldDefinitions(): void {
    // Use Sets to get unique values from the raw data
    const journeyStops = [...new Set((patientJourneyData as JourneyEvent[]).map(d => d.JOURNEY_STOP))];
    const referralStatuses = [...new Set((patientJourneyData as JourneyEvent[]).map(d => d.CURRENT_REFERRAL_STATUS))];
    const spDescriptions = [...new Set((patientJourneyData as JourneyEvent[]).map(d => d.SP_DESCRIPTION))];

    this.fieldDefinitions = [
      { value: 'STATUS_REASON_CODE', name: 'Status', type: 'status', operators: ['is one of', 'is not one of', 'duration between', 'First Status', 'Last Status', 'followed by', 'followed by directly'] },
      { value: 'STATUS_DATE', name: 'Status Date', type: 'date', operators: ['is on', 'is before', 'is after', 'is between', 'first', 'latest'] },
      { value: 'PATIENT_INITIATED_DATE', name: 'Initiated Date', type: 'date', operators: ['is on', 'is before', 'is after', 'is between', 'first', 'latest'] },
      {
        value: 'JOURNEY_STOP',
        name: 'Journey Stop',
        type: 'category',
        operators: ['is', 'is not'],
        options: journeyStops.map(val => ({ name: val, value: val }))
      },
      {
        value: 'CURRENT_REFERRAL_STATUS',
        name: 'Referral Status',
        type: 'category',
        operators: ['is', 'is not'],
        options: referralStatuses.map(val => ({ name: val, value: val }))
      },
      {
        value: 'SP_DESCRIPTION',
        name: 'SP Description',
        type: 'category',
        operators: ['is', 'is not'],
        options: spDescriptions.map(val => ({ name: val, value: val }))
      },
    ];
  }

  addRule(parentRuleSet: RuleSet): void {
    parentRuleSet.rules.push({
      type: 'rule',
      field: 'STATUS_REASON_CODE',
      operator: 'is one of',
      value: [],
      enabled: true,      // Default to enabled
      isEditing: true     // Default to edit mode so user can set it up
    });
    parentRuleSet.isCollapsed = false; // Auto-expand parent
  }

  // Method to add a nested ruleset ---
  addRuleSet(parentRuleSet: RuleSet): void {
    parentRuleSet.rules.push({
      type: 'ruleset',
      condition: 'and',
      rules: [],
      isCollapsed: false,
      name: 'Nested Ruleset',
      enabled: true // Default to enabled
    });
    parentRuleSet.isCollapsed = false;
  }

  clearAllRules(): void {
    // Reset root container
    this.ruleSet = {
      type: 'ruleset',
      condition: 'and',
      rules: [],
      isCollapsed: false
    };

    // Re-initialize with one Primary Ruleset containing one rule
    const primaryGroup: RuleSet = {
      type: 'ruleset',
      condition: 'and',
      rules: [],
      isCollapsed: false,
      enabled: true
    };
    this.addRule(primaryGroup);
    this.ruleSet.rules.push(primaryGroup);
  }

  isTopLevel(item: QueryItem): boolean {
    // It's top level if it's a direct child of the main ruleSet
    return this.ruleSet.rules.includes(item);
  }

  // Updated Drop Handler: Ensures we modify the specific array connected to the drop list
  drop(event: CdkDragDrop<QueryItem[]>) {
    if (event.previousContainer === event.container) {
      // Moving inside the same list
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // Moving from one list to another
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  // --- NEW: Toggle logic ---
  toggleCollapse(ruleset: RuleSet): void {
    ruleset.isCollapsed = !ruleset.isCollapsed;
  }

  toggleRuleEdit(rule: Rule): void {
    rule.isEditing = !rule.isEditing;
  }

  public getReadableValue(rule: Rule): string {
    if (rule.field === 'STATUS_REASON_CODE' && (rule.operator === 'followed by' || rule.operator === 'followed by directly')) {
      if (Array.isArray(rule.value) && rule.value.every(v => Array.isArray(v))) {
        // It's a sequence of arrays
        const steps = rule.value.map((step: any[]) => this.getStatusDisplay(step, 'Any'));
        return steps.join(rule.operator === 'followed by directly' ? ' -> (Direct) -> ' : ' -> (Gap) -> ');
      }
    }
    if (rule.field === 'STATUS_REASON_CODE' && rule.operator === 'duration between') {
      const from = this.getStatusDisplay(rule.startStatus, 'Start');
      const to = this.getStatusDisplay(rule.endStatus, 'End');
      let valDisplay = '';
      if (rule.operatorType === 'BETWEEN') {
        valDisplay = `between ${rule.min_days} and ${rule.max_days} days`;
      } else {
        valDisplay = `${rule.operatorType} ${rule.value} days`;
      }
      return `${from} -> ${to} (${valDisplay})`;
    }
    if (Array.isArray(rule.value)) {
      return rule.value.length ? `${rule.value.length} items selected` : 'None';
    }
    if (typeof rule.value === 'object' && rule.value?.start) {
      return `${new Date(rule.value.start).toLocaleDateString()} - ${new Date(rule.value.end).toLocaleDateString()}`;
    }
    return rule.value || 'Empty';
  }

  removeItem(parentRuleSet: RuleSet, index: number): void {
    parentRuleSet.rules.splice(index, 1);
  }

  // This function now handles resetting the rule structure when the operator changes
  onOperatorChange(rule: Rule): void {
    if (rule.operator === 'duration between') {
      rule.value = undefined; // Will use for single value in <=, >=, =
      rule.startStatus = [];
      rule.endStatus = [];
      // Initialize with new defaults
      rule.operatorType = 'BETWEEN';
      rule.min_days = 1;
      rule.max_days = 5;
      rule.value = 1; // Default for single value operators
      
      // Legacy properties cleanup if needed, or keep for overlap
      rule.days = undefined;
      rule.durationOperator = undefined;
    } else if (rule.operator === 'is between') {
      rule.value = { start: null, end: null };
      rule.startStatus = undefined;
      rule.endStatus = undefined;
      rule.days = undefined;
      rule.durationOperator = undefined;
      rule.operatorType = undefined;
      rule.min_days = undefined;
      rule.max_days = undefined;
    } else if (rule.operator === 'first' || rule.operator === 'latest') {
      rule.value = null;
      rule.startStatus = undefined;
      rule.endStatus = undefined;
      rule.days = undefined;
      rule.durationOperator = undefined;
      rule.operatorType = undefined;
      rule.min_days = undefined;
      rule.max_days = undefined;
    } else if (rule.operator === 'followed by' || rule.operator === 'followed by directly') {
      // Initialize sequence with 2 empty steps
      rule.value = [[], []];
      rule.startStatus = undefined;
      rule.endStatus = undefined;
      rule.days = undefined;
      rule.durationOperator = undefined;
      rule.operatorType = undefined;
      rule.min_days = undefined;
      rule.max_days = undefined;
    } else {
      rule.startStatus = undefined;
      rule.endStatus = undefined;
      rule.days = undefined;
      rule.durationOperator = undefined;
      rule.operatorType = undefined;
      rule.min_days = undefined;
      rule.max_days = undefined;
      const definition = this.getFieldDefinition(rule.field);
      if (definition?.type === 'status') {
        rule.value = [];
      } else {
        rule.value = null;
      }
    }
  }

  onFieldChange(rule: Rule): void {
    const definition = this.getFieldDefinition(rule.field);
    if (definition) {
      rule.operator = definition.operators[0];
      rule.value = definition.type === 'status' ? [] : null;
    }
  }

  getFieldDefinition(fieldValue: string): FieldDefinition | undefined {
    return this.fieldDefinitions.find(f => f.value === fieldValue);
  }

  updateStatusRuleValue(rule: Rule, selector: StatusSelectorComponent, statusType: 'value' | 'start' | 'end', stepIndex?: number): void {
    const selected = selector.getCheckedReasons();
    if (statusType === 'value') {
      if (typeof stepIndex === 'number' && Array.isArray(rule.value)) {
        // Update specific step in sequence
        rule.value[stepIndex] = selected;
      } else {
        rule.value = selected;
      }
    } else if (statusType === 'start') {
      rule.startStatus = selected;
    } else {
      rule.endStatus = selected;
    }
  }

  // --- Sequence Helper Methods ---
  addSequenceStep(rule: Rule): void {
    if (!Array.isArray(rule.value)) {
      rule.value = [[], []];
    }
    rule.value.push([]);
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }

  removeSequenceStep(rule: Rule, index: number): void {
    if (Array.isArray(rule.value) && rule.value.length > 2) {
      rule.value.splice(index, 1);
    }
  }

  public getStatusDisplay(statuses: StatusReason[] | undefined, placeholder: string = 'Select Status(es)'): string {
    if (!statuses || statuses.length === 0) return placeholder;
    if (statuses.length === 1) return statuses[0].desc;
    return `${statuses.length} statuses selected`;
  }

  public getStatusColor(statuses: StatusReason[] | undefined): string {
    return (statuses && statuses.length > 0) ? statuses[0].color : '#ccc';
  }

  onCancelClick(): void {
    if (this.activeTab === 'statuses') {
      if (this.statusSelector) {
        this.statusSelector.clearSelections();
      }
    } else if (this.activeTab === 'query') {
      this.disableAllRules(this.ruleSet);
    }
  }

  // Helper to recursively disable all rules
  private disableAllRules(ruleSet: RuleSet): void {
     if (!ruleSet.rules) return;
     ruleSet.rules.forEach(item => {
        if (item.type === 'ruleset') {
           (item as RuleSet).enabled = false; // Disable the ruleset itself
           this.disableAllRules(item as RuleSet); // Recurse
        } else {
           (item as Rule).enabled = false;
        }
     });
  }

  onCloseClick(): void {
    this.dialogRef.close({ action: 'close' });
  }

  // Snapshot of the hierarchy at the last "Apply" (or at open)
  private snapshotHierarchy!: ReferralLayer[];
  public showAppliedMessage = false;

  // --- Standard Dialog Methods ---
  onHierarchyChange(updatedHierarchy: ReferralLayer[]): void {
    this.localHierarchy = updatedHierarchy;
    this.data.updateCallback(this.localHierarchy);
  }

  onManageStatusApplyClick(): void {
    // 1. Commit the changes: Update the snapshot to match the current local state
    this.snapshotHierarchy = this.cloneHierarchy(this.localHierarchy);
    
    // 2. Sync baseHierarchy so Query Builder sees the new colors/statuses
    this.baseHierarchy = this.cloneHierarchy(this.localHierarchy);

    // 3. Show feedback
    this.showAppliedMessage = true;
    setTimeout(() => {
      this.showAppliedMessage = false;
    }, 3000);

    // 4. Do NOT close the dialog. The user must close it manually or click X.
    // The "Apply" button essentially says "Keep these changes even if I close now".
  }

  onQueryApplyClick(applyToDb: boolean = true): void {
    // 0. Generate SQL Query using the UI-friendly RuleSet (must match generateSqlQuery expectations)
    const sqlQuery = applyToDb ? this.generateSqlQuery(this.ruleSet) : undefined;
    
    // 1. Clone the RuleSet so we don't mutate the UI state
    const ruleSetToSend = JSON.parse(JSON.stringify(this.ruleSet));
    
    // 2. Augment the CLONED RuleSet with 'Advanced'/'Basic' types and Mapped Operators
    this.augmentRulesAndApplyType(ruleSetToSend);

    const result = {
      hierarchy: this.localHierarchy,
      ruleSet: ruleSetToSend,
      sqlQuery: sqlQuery,
      action: 'queryApply', // --- Explicit Action Flag ---
      param: { filter_query: sqlQuery }
    };
    this.dialogRef.close(result);
  }

  onQueryResetClick(): void {
    this.clearAllRules();
    // Reset hierarchy to the base state (original passed data)
    this.localHierarchy = this.cloneHierarchy(this.baseHierarchy);
    const sqlQuery = null;
    const result = {
      hierarchy: this.localHierarchy,
      ruleSet: this.ruleSet,
      sqlQuery: sqlQuery,
      action: 'queryReset', // --- Explicit Action Flag ---
      param: { filter_query: sqlQuery }
    };
    this.dialogRef.close(result);
  }

  // --- SQL Generation Logic ---

  private generateSqlQuery(ruleSet: RuleSet): string {
    const queryObject = this.buildQueryObject(ruleSet);
    // Wrap in "where" as per requirements
    const finalObj = { where: queryObject };
    return JSON.stringify(finalObj);
  }

  private buildQueryObject(item: Rule | RuleSet): any {
    if (item.type === 'ruleset') {
      const rs = item as RuleSet;
      // Filter out disabled rules or empty rulesets if needed?
      // For now, process all.
      const children = rs.rules.map(r => this.buildQueryObject(r)).filter(r => r !== null);

      if (children.length === 0) return null;

      // If it's the top level, we might want to wrap it in "where"
      // But the recursive structure is: { logic: 'AND', rules: [...] }
      // The top level wrapper seems to be { where: { ... } } based on the example.
      // However, this method returns the inner structure.

      // Check if we are processing the root ruleset? 
      // The example shows: { "where": { "logic": "AND", "ruleSets": [...] } }
      // Wait, the example has "ruleSets" property?
      // Example 1: "{'where':{'logic':'AND','rules':[{...}]}}"
      // Example 2: '{"where":{"logic":"AND","ruleSets":[{"logic":"AND","rules":[{...}]}]}}'

      // It seems "rules" can contain both rules and nested rulesets? 
      // Or does it distinguish?
      // Standard query builders often use "rules" array for both.
      // But the example 2 has "ruleSets".

      // Let's look at the structure closely:
      // "ruleSets": [ { "logic": "AND", "rules": [...] } ]

      // It seems it separates them?
      // Let's assume a standard structure where "rules" contains everything, 
      // UNLESS the backend specifically requires "ruleSets" for nested groups.

      // Given the user's example 2:
      // "ruleSets": [ ... ]

      // I will try to separate them if that's what's implied, 
      // OR I'll stick to a common structure if "ruleSets" was just an artifact of that specific example.
      // Actually, looking at Example 1, it has "rules": [ ... ].

      // Let's try to produce a structure that matches the input `RuleSet` structure.
      // The input has `rules: (Rule | RuleSet)[]`.

      const rules = children.filter(c => !c.logic); // Leaves (Rules)
      const ruleSets = children.filter(c => c.logic); // Nodes (RuleSets)

      const result: any = {
        logic: rs.condition.toUpperCase()
      };

      if (rules.length > 0) result.rules = rules;
      if (ruleSets.length > 0) result.ruleSets = ruleSets;

      // If we are at the very top, we need to wrap in "where".
      // But this function is recursive.
      // We'll handle the "where" wrapper in `generateSqlQuery`.

      return result;
    } else {
      const rule = item as Rule;
      if (!rule.enabled) return null;

      const column = this.mapFieldToColumn(rule.field);
      let operator = this.mapOperator(rule.operator);
      const type = this.getFieldType(rule.field);

      // Handle "duration between" special case
      // Handle "duration between" special case
      if (rule.operator === 'duration between') {
        const fromStatus = rule.startStatus ? rule.startStatus.map(s => s.code).join(',') : '';
        const toStatus = rule.endStatus ? rule.endStatus.map(s => s.code).join(',') : '';
        
        const baseResult: any = {
          column_operator: 'DURATION_BETWEEN_STATUS',
          rule_type: 'Advanced',
          operatorType: rule.operatorType || 'BETWEEN', // Default to BETWEEN
          fromStatus: fromStatus,
          toStatus: toStatus
        };

        if (rule.operatorType === 'BETWEEN') {
           baseResult.min_days = rule.min_days;
           baseResult.max_days = rule.max_days;
        } else {
           // For =, <=, >=, etc.
           baseResult.value = rule.value;
        }

        return baseResult;
      }

      let value = rule.value;
      let finalType = type;
      let ruleType = 'Basic'; // Default to Basic

      // Check for Advanced Rules
       if (operator === 'FIRST_STATUS' || operator === 'LAST_STATUS') {
        ruleType = 'Advanced';
       }



        // Check for Sequential Rules
        if (rule.operator === 'followed by' || rule.operator === 'followed by directly') {
             
             const codes: string[] = [];
             if (Array.isArray(value)) {
               value.forEach((step: any[]) => {
                 if (Array.isArray(step)) {
                   step.forEach((s: any) => codes.push(s.code));
                 }
               });
             }
             
             let colOp = 'STATUS_CODE_SEQUENCE';
             if (rule.operator === 'followed by directly') {
                colOp = 'STATUS_CODE_EXACT_SEQUENCE';
             }

             return {
                column_operator: colOp,
                rule_type: 'Advanced',
                value: codes.join(',')
             };
        }

       if (ruleType === 'Advanced') {
         // Special handling for Advanced Rules
         return {
           column_operator: operator,
           rule_type: ruleType,
           value: (Array.isArray(value) && value.length === 1) ? value[0].code : (Array.isArray(value) && value.length > 0 ? value.map((v: any) => v.code).join(',') : value)
         };
       }

      // Handle Status values (Array of objects -> Array of strings or single string)
      if (type === 'status') {
        finalType = 'string'; // Backend expects 'string' for status codes
        if (Array.isArray(value)) {
          const codes = value.map((v: any) => v.code);
          if (codes.length === 0) {
            // No status selected?
            return null;
          } else if (codes.length === 1) {
            value = codes[0];
            // Optimize operator for single value
            if (operator === 'IN') operator = '=';
            if (operator === 'NOT IN') operator = '!=';
          } else {
            value = codes.join(',');
            // Ensure operator is IN/NOT IN
            if (operator === '=') operator = 'IN';
            if (operator === '!=') operator = 'NOT IN';
          }
        }
      }

      // Format value
      if (type === 'date') {
        // Ensure YYYY-MM-DD
        if (value && value.start) {
          // Range? "is between"
          // The example shows "operator": ">", "value": "2021-03-10"
          // If it's a range, we might need two rules or a BETWEEN operator.
          // My `Rule` object for date range uses `value: { start, end }`.
          if (rule.operator === 'is between') {
            // We might need to return TWO rules? Or a single BETWEEN rule?
            // SQL: col BETWEEN val1 AND val2
            // The JSON format: "operator": "BETWEEN", "value": ["val1", "val2"] ?
            // Or "value": "val1,val2"?
            // Let's assume array for BETWEEN.
            value = [this.formatDate(value.start), this.formatDate(value.end)];
          } else {
            value = this.formatDate(value);
          }
        } else if (value instanceof Date || typeof value === 'string') {
          value = this.formatDate(value);
        }
      }

      return {
        column: column,
        operatorType: operator,
        value: value,
        type: finalType,
        rule_type: ruleType
      };
    }
  }

  private mapFieldToColumn(field: string): string {
    const map: { [key: string]: string } = {
      'STATUS_REASON_CODE': 'status_reas_cd',
      'STATUS_DATE': 'tran_dt',
      'PATIENT_INITIATED_DATE': 'patient_init_dt',
      'JOURNEY_STOP': 'journey_stop',
      'CURRENT_REFERRAL_STATUS': 'curr_ref_stat',
      'SP_DESCRIPTION': 'sp_desc'
    };
    return map[field] || field;
  }

  private getFieldType(field: string): string {
    const def = this.getFieldDefinition(field);
    return def ? def.type : 'string';
  }

  private mapOperator(operator: string): string {
    const map: { [key: string]: string } = {
      'is one of': 'IN',
      'is not one of': 'NOT IN',
      'is on': '=',
      'is before': '<',
      'is after': '>',
      'is between': 'BETWEEN',
      'is': '=',
      'is not': '!=',
      'first': 'FIRST',
      'latest': 'LATEST',
      'First Status': 'FIRST_STATUS',
      'Last Status': 'LAST_STATUS'
    };
    return map[operator] || '=';
  }

  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    // YYYY-MM-DD
    return d.toISOString().split('T')[0];
  }


  private cloneHierarchy(data: ReferralLayer[]): ReferralLayer[] {
    return JSON.parse(JSON.stringify(data));
  }


  defaultMockRuleset: RuleSet = { type: 'ruleset', condition: 'and', rules: [], isCollapsed: false };

  savedQueries: SavedQueryItem[] = [
    { id: 'q1', name: 'Discontinuations', isActive: true, isSelected: false, sharedWithCount: 2, groupCount: 2, rulesetCount: 2, ruleCount: 13, queryData: this.defaultMockRuleset },
    { id: 'q2', name: 'Cancellations', isActive: false, isSelected: true, sharedWithCount: 3, groupCount: 3, rulesetCount: 3, ruleCount: 10, queryData: this.defaultMockRuleset },
    { id: 'q3', name: 'Fallback Logic', isActive: false, isSelected: false, sharedWithCount: 4, groupCount: 0, rulesetCount: 4, ruleCount: 18, queryData: this.defaultMockRuleset },
  ];

  // --- ADD THESE METHODS ---

  /**
   * Handles the "Save a Query" button click in the Query Builder tab.
   * Creates a new query entry and switches to the list tab.
   */
  saveQueryAndSwitchTab(): void {
    // 1. Calculate stats based on current ruleSet (simplified counting for demo)
    // In a real scenario, you'd traverse this.ruleSet to get accurate counts.
    const currentRules = this.ruleSet.rules.filter(r => r.type === 'rule').length;
    const currentSets = this.ruleSet.rules.filter(r => r.type === 'ruleset').length;

    const newQuery: SavedQueryItem = {
      id: Date.now().toString(),
      name: `Query List #1 (Saved ${new Date().toLocaleTimeString()})`, // Example name generation
      isActive: true,
      isSelected: false,
      sharedWithCount: 0, // Default
      groupCount: currentSets > 0 ? 1 : 0, // Simplified logic
      rulesetCount: currentSets,
      ruleCount: currentRules,
      queryData: JSON.parse(JSON.stringify(this.ruleSet)) // <--- SAVE THE DATA DEEP COPY
    };

    // 2. Add the new query to the top of the list
    this.savedQueries.unshift(newQuery);

    // 3. Switch to the List tab
    this.activeTab = 'list';

    // Optional: Clear the query builder after saving?
    // this.clearAllRules();
  }

  // 4. NEW LOAD FUNCTION
  loadQuery(query: SavedQueryItem): void {
    // Deep copy the stored data back into the main ruleSet variable
    this.ruleSet = JSON.parse(JSON.stringify(query.queryData));

    // Switch tab to view it
    this.activeTab = 'query';
  }

  /**
   * Handles dropping list items to reorder them.
   */
  dropQueryListItem(event: CdkDragDrop<SavedQueryItem[]>) {
    moveItemInArray(this.savedQueries, event.previousIndex, event.currentIndex);
  }


  /**
   * Clones an existing query and inserts it immediately after the original.
   */
  cloneQuery(query: SavedQueryItem): void {
    // 1. Create a deep copy of the object
    const newQuery: SavedQueryItem = JSON.parse(JSON.stringify(query));

    // 2. Update unique fields
    newQuery.id = Date.now().toString(); // Generate a new unique ID
    newQuery.name = `${query.name} (Copy)`; // Indicate it's a copy
    newQuery.isSelected = false; // Reset selection

    // 3. Find the index of the original query
    const index = this.savedQueries.indexOf(query);

    // 4. Insert the new query right after the original
    if (index !== -1) {
      this.savedQueries.splice(index + 1, 0, newQuery);
    } else {
      // Fallback: add to top if original not found
      this.savedQueries.unshift(newQuery);
    }
  }


  /**
   * Deletes the specified query from the list.
   */
  deleteQuery(query: SavedQueryItem): void {
    const index = this.savedQueries.indexOf(query);
    if (index !== -1) {
      this.savedQueries.splice(index, 1);
    }
  }

  // Helper for template
  hasRules(ruleset: RuleSet): boolean {
    return ruleset.rules && ruleset.rules.some(r => r.type === 'rule');
  }

  // --- Share View Methods ---

  openShareView(query: SavedQueryItem): void {
    this.selectedQuery = query;
    this.activeTab = 'share';
    this.searchQuery = '';
  }

  goBackToQueryList(): void {
    this.activeTab = 'list';
    this.selectedQuery = null;
  }

  toggleUser(user: any): void {
    user.selected = !user.selected;
  }

  selectAllUsers(): void {
    this.users.forEach(u => u.selected = true);
  }

  deselectAllUsers(): void {
    this.users.forEach(u => u.selected = false);
  }

  get filteredUsers() {
    if (!this.searchQuery) return this.users;
    return this.users.filter(u => u.name.toLowerCase().includes(this.searchQuery.toLowerCase()));
  }

  get selectedUserCount(): number {
    return this.users.filter(u => u.selected).length;
  }

  shareQuery(): void {
    if (this.selectedQuery) {
      // Update the mock count
      this.selectedQuery.sharedWithCount = this.selectedUserCount;
      // Here you would typically make an API call to save the sharing settings
    }
    this.goBackToQueryList();
  }

  // --- AI Tab Methods ---
  public aiInput: string = '';

  submitAiQuery(): void {
    if (this.aiInput.trim()) {
      this.dialogRef.close({
        action: 'aiQuery',
        param: { ai_prompt: this.aiInput }
      });
    }
  }

  onFilterQueryChange(query: string) {
    // This might be needed if we want to support two-way binding or updates from parent
  }
  /**
   * Helper to find a StatusReason object by its code
   */
  public findStatusReasonByCode(code: string): StatusReason | null {
    if (!this.baseHierarchy) return null;
    for (const referral of this.baseHierarchy) {
      for (const group of referral.groups) {
        for (const subGroup of group.subGroups) {
          const found = subGroup.reasons.find(r => r.code === code);
          if (found) return found;
        }
      }
    }
    return null;
  }

  // --- Helper to Augment Rules with Type Info ---
  private augmentRulesAndApplyType(ruleSet: RuleSet): void {
    if (!ruleSet.rules || ruleSet.rules.length === 0) return;

    for (const item of ruleSet.rules) {
      if (item.type === 'ruleset') {
        this.augmentRulesAndApplyType(item as RuleSet);
      } else {
        const rule = item as Rule;
        // Only map if it's NOT a special operator like 'duration between' which mapOperator defaults to '='
        let operator = rule.operator;
        if (rule.operator !== 'duration between') {
             operator = this.mapOperator(rule.operator);
        }
        
        // Logic duplicated from buildQueryObject to ensure consistency
        if (rule.field === 'STATUS_REASON_CODE') {
          if (operator === 'FIRST_STATUS' || operator === 'LAST_STATUS') {
            rule.rule_type = 'Advanced';
            rule.operator = operator; // Force the operator to be the mapped value (FIRST_STATUS) for logic
            
            // Flatten the value for Advanced Rules (Array of Objs -> Single String Code)
            if (Array.isArray(rule.value) && rule.value.length > 0) {
                 rule.value = rule.value[0].code;
            }
          } else if (rule.operator === 'followed by' || rule.operator === 'followed by directly') {
             rule.rule_type = 'Advanced';
          } else if (rule.operator === 'duration between') {
             rule.rule_type = 'Advanced';
          } else {
            rule.rule_type = 'Basic';
          }
        } else {
          rule.rule_type = 'Basic';
        }
      }
    }
  }

  private reverseMapOperator(operator: string, field: string): string {
    const map: { [key: string]: string } = {
       'FIRST_STATUS': 'First Status',
       'LAST_STATUS': 'Last Status',
       'IN': 'is one of',
       'NOT IN': 'is not one of',
       '=': 'is', // Default, logic below refines
       '!=': 'is not',
       '<': 'is before',
       '>': 'is after',
       'BETWEEN': 'is between',
       'FIRST': 'first',
       'LATEST': 'latest'
    };

    let mapped = map[operator] || operator;

    // Refine based on field type if ambiguous
    const def = this.getFieldDefinition(field);
    if (def) {
       if (operator === '=') {
           if (def.type === 'date') mapped = 'is on';
           else if (def.type === 'category') mapped = 'is';
       }
    }
    return mapped;
  }

  private hydrateRuleSet(ruleSet: RuleSet): void {
     if (!ruleSet.rules) return;

     ruleSet.rules.forEach(item => {
        if (item.type === 'ruleset') {
           if ((item as RuleSet).enabled === undefined) {
             (item as RuleSet).enabled = true;
           }
           this.hydrateRuleSet(item as RuleSet);
        } else {
           const rule = item as Rule;
           
           // 1. Restore Operator Name (e.g. FIRST_STATUS -> First Status)
           rule.operator = this.reverseMapOperator(rule.operator, rule.field);

           // 2. Restore Value (Un-flatten for Status Rules)
           if (rule.field === 'STATUS_REASON_CODE') {
              if (rule.operator === 'First Status' || rule.operator === 'Last Status') {
                  // If it's a string code, convert back to [StatusReason]
                  if (typeof rule.value === 'string') {
                      const reason = this.findStatusReasonByCode(rule.value);
                      rule.value = reason ? [reason] : [];
                  }
              }
              // You might also need to handle 'IN' -> 'is one of' if the value was flattened to strings?
              // Currently augmentRules doesn't flatten basic rules (PixelMap handles it).
              // BUT if we wanted to be safe, we could check if value is string[] vs StatusReason[]
           }
        }
     });
  }

}